import React from 'react';
import { propertyTypes, bathTypes } from '@sparklib';
import { Icon } from '@assets';

function formatPrice(val) {
  if (!val) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(val);
}

function priceRange(minPrice, maxPrice) {
  if (minPrice && maxPrice && minPrice !== maxPrice)
    return `${formatPrice(minPrice)} – ${formatPrice(maxPrice)}`;
  if (minPrice) return `From ${formatPrice(minPrice)}`;
  if (maxPrice) return `Up to ${formatPrice(maxPrice)}`;
  return 'Price unavailable';
}

// Returns only the available floor plans that match all active filter criteria.
// Falls back to the full list when no filters are active.
function filterFloorPlans(floorPlans, af) {
  if (!af || !floorPlans) return floorPlans;
  const { bedTypes, minPrice, maxPrice, minSqft, availFrom, availTo } = af;
  const hasFilter = bedTypes?.length || minPrice > 0 || maxPrice > 0 || minSqft > 0 || availFrom || availTo;
  if (!hasFilter) return floorPlans;
  // Compute once for the avail_from check below
  const todayStart = availFrom ? new Date(new Date().setHours(0, 0, 0, 0)) : null;
  return floorPlans.filter(fp => {
    if (!fp.isAvailable) return false;
    if (bedTypes?.length && !bedTypes.includes(fp.bed)) return false;
    const rent = fp.rentMin || fp.rentMax || 0;
    if (maxPrice > 0 && rent > 0 && rent > maxPrice) return false;
    const rentTop = fp.rentMax || fp.rentMin || 0;
    if (minPrice > 0 && rentTop > 0 && rentTop < minPrice) return false;
    if (minSqft > 0 && fp.sqftMin > 0 && fp.sqftMin < minSqft) return false;
    // avail_from: only exclude if available_to is in the future AND closes before move-in date.
    // A past available_to means the unit has been open since that date and is still available now.
    if (availFrom && fp.availableTo) {
      const fpAvailTo = new Date(fp.availableTo);
      if (fpAvailTo >= todayStart && fpAvailTo < availFrom) return false;
    }
    // avail_to: floor plan must open availability before user's deadline
    if (availTo && fp.availableFrom && new Date(fp.availableFrom) > availTo) return false;
    return true;
  });
}

function commissionLine(sendEscort, escort, bonus) {
  const s = sendEscort != null ? `${sendEscort}%` : '-';
  const e = escort     != null ? `${escort}%`     : '-';
  const b = bonus      != null ? `$${bonus}`       : '-';
  return `Send: ${s} | Escort: ${e} | Flat Fee: ${b}`;
}

export default function PropertyCard({ property, activeFilters, selectedProperties, toggleSelection, onRowClick }) {
  const {
    id, name, address, zip, builtYear, specials,
    minPrice, maxPrice, minSqft,
    sendEscort, escort, bonus,
    addonItems, imageSmallUrls, neighborhood, floorPlans,
  } = property;

  const img = imageSmallUrls && imageSmallUrls.length > 0 ? imageSmallUrls[0] : null;

  const isSelected = (selectedProperties || []).some(
    p => Number(p.propertyId) === Number(id)
  );

  // Floor plans filtered to only those matching active filter criteria.
  const displayPlans = filterFloorPlans(floorPlans, activeFilters);
  const hasDisplayPlans = displayPlans && displayPlans.length > 0;

  // Compute price range from filtered floor plans; fall back to property-level aggregates.
  const displayMinPrice = hasDisplayPlans
    ? Math.min(...displayPlans.map(fp => fp.rentMin || fp.rentMax || 0).filter(Boolean))
    : minPrice;
  const displayMaxPrice = hasDisplayPlans
    ? Math.max(...displayPlans.map(fp => fp.rentMax || fp.rentMin || 0).filter(Boolean))
    : maxPrice;

  return (
    <div
      className='property-card position-relative p-0'
      style={{ cursor: 'pointer' }}
      onClick={() => onRowClick && onRowClick(property)}
    >
      <div className='card-img position-relative overflow-hidden'>
        {img ? (
          <img src={img} alt={name} className='fp-card-img' />
        ) : (
          <div className='fp-no-image'>No Image</div>
        )}
        {specials && <span className='fp-specials-badge'>Special</span>}
        <div className='like-btn' onClick={(e) => { e.stopPropagation(); toggleSelection && toggleSelection(property); }}>
          {isSelected
            ? <Icon icon='heart' size='lg' />
            : <Icon size='lg' icon={['far', 'heart']} />
          }
        </div>
        <div className='d-flex justify-content-between position-absolute property-report-image-overlay' style={{ bottom: 0 }}>
          <div>
            <h6 className='mb-0'>{address}{zip ? `, ${zip}` : ''}</h6>
          </div>
        </div>
      </div>

      <h4 className='mb-0 mt-2'>{name}</h4>
      {neighborhood && <p className='mb-1'>{neighborhood.name}</p>}

      <p className='mb-1' style={{ fontSize: 13, color: 'var(--gray-500)' }}>
        {commissionLine(sendEscort, escort, bonus)}
      </p>

      <p className='mb-1 font-weight-600'>{priceRange(displayMinPrice, displayMaxPrice)}</p>

      {hasDisplayPlans && (
        <div className='d-flex align-items-center mb-1' style={{ gap: 8, fontSize: 13 }}>
          <span>{propertyTypes(displayPlans)} <Icon icon='bed' /></span>
          <span>{bathTypes(displayPlans)} <Icon icon='bath' /></span>
          {minSqft && <small className='text-muted'>{minSqft.toLocaleString()}+ sqft</small>}
        </div>
      )}

      {specials && (
        <p className='mb-1' style={{ fontSize: 13 }}>{specials}</p>
      )}

      {addonItems && addonItems.length > 0 && (
        <div className='d-flex flex-wrap' style={{ gap: 4 }}>
          {addonItems.slice(0, 3).map((a) => (
            <span key={a} className='badge badge-secondary mr-1 mb-1'>{a}</span>
          ))}
          {addonItems.length > 3 && (
            <small className='text-muted'>+{addonItems.length - 3} more</small>
          )}
        </div>
      )}

      {builtYear && <small className='text-muted'>Built {builtYear}</small>}
    </div>
  );
}
